import { useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import './MapPage.css';

export default function MapPage({ user, refreshSignal, onNavigate, currentProfile }) {
  const containerRef = useRef(null);
  const onNavigateRef = useRef(onNavigate);
  const [sdkError, setSdkError] = useState(false);

  // onNavigate 최신값 유지 (deps 배열 없이 클릭 핸들러에서 사용)
  useEffect(() => { onNavigateRef.current = onNavigate; }, [onNavigate]);

  useEffect(() => {
    if (!window.kakao) {
      setSdkError(true);
      return;
    }

    window.kakao.maps.load(() => {
      if (!containerRef.current) return;

      const map = new window.kakao.maps.Map(containerRef.current, {
        center: new window.kakao.maps.LatLng(36.5, 127.8),
        level: 13,
      });

      const fetchAndMark = async () => {
        let records = [];

        if (currentProfile?.id) {
          // record_shares 기준으로 현재 프로필에 표시된 기록만
          const { data: shares } = await supabase
            .from('record_shares')
            .select('record_id')
            .eq('profile_id', currentProfile.id);
          const sharedIds = shares?.map((s) => s.record_id) || [];

          if (sharedIds.length > 0) {
            const { data } = await supabase
              .from('records')
              .select('id, title, category_name, category_emoji, location_name, latitude, longitude')
              .eq('user_id', user.id)
              .in('id', sharedIds)
              .not('latitude', 'is', null)
              .not('longitude', 'is', null);
            records = data || [];
          }
        } else {
          // 프로필 미설정 fallback: 전체
          const { data } = await supabase
            .from('records')
            .select('id, title, category_name, category_emoji, location_name, latitude, longitude')
            .eq('user_id', user.id)
            .not('latitude', 'is', null)
            .not('longitude', 'is', null);
          records = data || [];
        }
        if (records.length === 0) return;

        const bounds = new window.kakao.maps.LatLngBounds();

        records.forEach((record) => {
          const pos = new window.kakao.maps.LatLng(record.latitude, record.longitude);
          bounds.extend(pos);

          const content = document.createElement('div');
          content.className = 'map-label';
          const catPart = (record.category_emoji || record.category_name)
            ? `(${record.category_emoji || ''}${record.category_name || ''})`
            : '';
          content.innerHTML = `
            <div class="map-label-bubble">
              ${catPart ? `<span class="map-label-cat">${catPart}</span>` : ''}
              <span class="map-label-title">${record.title}</span>
            </div>
            <div class="map-label-stem"></div>
            <div class="map-label-dot"></div>
          `;

          content.addEventListener('click', () => {
            onNavigateRef.current('editRecord', record);
          });

          const overlay = new window.kakao.maps.CustomOverlay({
            content,
            position: pos,
            yAnchor: 1,
            zIndex: 3,
          });

          overlay.setMap(map);
        });

        map.setBounds(bounds);
      };

      fetchAndMark();
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id, refreshSignal, currentProfile?.id]);

  return (
    <div className="map-page">
      <header className="map-header">
        <span className="map-header-title">지도</span>
        {currentProfile && (
          <button
            className="map-profile-btn"
            onClick={() => onNavigate('profileManage')}
          >
            <span className="map-profile-emoji">{currentProfile.emoji}</span>
            <span className="map-profile-name">{currentProfile.name}</span>
          </button>
        )}
      </header>
      {sdkError ? (
        <div className="map-error">카카오맵 SDK를 불러올 수 없습니다.</div>
      ) : (
        <div ref={containerRef} className="map-container" />
      )}
    </div>
  );
}
